import type { CombinedError } from "urql";
import type { VariablesOf } from "@/data-access/graphql";
import {
  CreateBriefingDefinition,
  DeleteBriefingDefinition,
  GetBriefingDefinition,
  PauseBriefingDefinition,
  ResumeBriefingDefinition,
  UpdateBriefingDefinition,
} from "@/data-access/scheduled-briefings/queries/briefing-definition-queries";
import {
  mapBriefingDefinitionDeleteMutationResponse,
  mapBriefingDefinitionGraphQLError,
  mapBriefingDefinitionMutationResponse,
  mapGetBriefingDefinitionResponse,
} from "@/lib/scheduled-briefings/repository/briefingDefinitionMappers";
import type {
  BriefingDefinitionRepository,
  BriefingDefinitionRepositoryError,
  BriefingDefinitionRepositoryResult,
} from "@/lib/scheduled-briefings/repository/briefingDefinitionRepository.types";
import { client } from "@/lib/urqlProvider";
import type {
  BriefingDefinitionCreateInput,
  BriefingDefinitionUpdateInput,
} from "@/types/briefing-definition/briefingDefinition.types";

type CreateBriefingDefinitionVariables = VariablesOf<
  typeof CreateBriefingDefinition
>;
type UpdateBriefingDefinitionVariables = VariablesOf<
  typeof UpdateBriefingDefinition
>;

const requestFailure = (
  message: string,
): BriefingDefinitionRepositoryError => ({
  code: "REQUEST_FAILURE",
  message,
});

const toCreateMutationInput = (
  input: BriefingDefinitionCreateInput,
): CreateBriefingDefinitionVariables["input"] => ({
  templateId: input.templateId,
  templateUpdatedAt: input.templateUpdatedAt,
  name: input.name,
  recipients: input.recipients,
  sendAt: input.sendAt,
  sendOn: input.sendOn,
  timezone: input.timezone,
  slots: input.slots,
  imageAssets: input.imageAssets,
  requiresApproval: input.requiresApproval ?? false,
  ...(input.description !== undefined
    ? { description: input.description }
    : {}),
  ...(input.overallPrompt !== undefined
    ? { overallPrompt: input.overallPrompt }
    : {}),
  ...(input.lookbackDays !== undefined
    ? { lookbackDays: input.lookbackDays }
    : {}),
  ...(input.audience !== undefined ? { audience: input.audience } : {}),
});

const toUpdateMutationInput = (
  input: BriefingDefinitionUpdateInput,
): UpdateBriefingDefinitionVariables["input"] => ({
  ...(input.name !== undefined ? { name: input.name } : {}),
  ...(input.description !== undefined
    ? { description: input.description }
    : {}),
  ...(input.overallPrompt !== undefined
    ? { overallPrompt: input.overallPrompt }
    : {}),
  ...(input.lookbackDays !== undefined
    ? { lookbackDays: input.lookbackDays }
    : {}),
  ...(input.slots !== undefined ? { slots: input.slots } : {}),
  ...(input.imageAssets !== undefined
    ? { imageAssets: input.imageAssets }
    : {}),
  ...(input.recipients !== undefined ? { recipients: input.recipients } : {}),
  ...(input.sendOn !== undefined ? { sendOn: input.sendOn } : {}),
  ...(input.sendAt !== undefined ? { sendAt: input.sendAt } : {}),
  ...(input.timezone !== undefined ? { timezone: input.timezone } : {}),
  ...(input.requiresApproval !== undefined
    ? { requiresApproval: input.requiresApproval }
    : {}),
  ...(input.audience !== undefined ? { audience: input.audience } : {}),
  ...(input.templateUpdatedAt !== undefined
    ? { templateUpdatedAt: input.templateUpdatedAt }
    : {}),
});

/** Shared error, missing-payload, and thrown-request handling for repository requests. */
const runBriefingDefinitionRequest = async <Payload, Data>(
  action: "lookup" | "create" | "update" | "pause" | "resume" | "delete",
  execute: () => Promise<{
    error: CombinedError | undefined;
    payload: Payload | undefined;
  }>,
  mapPayload: (payload: Payload) => BriefingDefinitionRepositoryResult<Data>,
): Promise<BriefingDefinitionRepositoryResult<Data>> => {
  try {
    const { error, payload } = await execute();

    if (error !== undefined) {
      return {
        success: false,
        error: mapBriefingDefinitionGraphQLError(error),
      };
    }
    if (payload === undefined) {
      return {
        success: false,
        error: requestFailure(
          `Briefing definition ${action} returned no data.`,
        ),
      };
    }

    return mapPayload(payload);
  } catch (error) {
    return {
      success: false,
      error: requestFailure(
        error instanceof Error
          ? error.message
          : `Briefing definition ${action} failed.`,
      ),
    };
  }
};

/** @public */
export const getGraphQLBriefingDefinition: BriefingDefinitionRepository["get"] =
  (_context, id) =>
    runBriefingDefinitionRequest(
      "lookup",
      async () => {
        const result = await client
          .query(
            GetBriefingDefinition,
            { id },
            { requestPolicy: "network-only" },
          )
          .toPromise();
        return { error: result.error, payload: result.data };
      },
      mapGetBriefingDefinitionResponse,
    );

/** @public */
export const createGraphQLBriefingDefinition: BriefingDefinitionRepository["create"] =
  (_context, input) =>
    runBriefingDefinitionRequest(
      "create",
      async () => {
        const result = await client
          .mutation(CreateBriefingDefinition, {
            input: toCreateMutationInput(input),
          })
          .toPromise();
        return {
          error: result.error,
          payload: result.data?.createBriefingDefinition,
        };
      },
      mapBriefingDefinitionMutationResponse,
    );

/** @public */
export const updateGraphQLBriefingDefinition: BriefingDefinitionRepository["update"] =
  (_context, input) =>
    runBriefingDefinitionRequest(
      "update",
      async () => {
        const result = await client
          .mutation(UpdateBriefingDefinition, {
            id: input.id,
            expectedUpdatedAt: input.expectedUpdatedAt,
            input: toUpdateMutationInput(input),
          })
          .toPromise();
        return {
          error: result.error,
          payload: result.data?.updateBriefingDefinition,
        };
      },
      mapBriefingDefinitionMutationResponse,
    );

/** @public */
export const pauseGraphQLBriefingDefinition: BriefingDefinitionRepository["pause"] =
  (_context, id) =>
    runBriefingDefinitionRequest(
      "pause",
      async () => {
        const result = await client
          .mutation(PauseBriefingDefinition, { id })
          .toPromise();
        return {
          error: result.error,
          payload: result.data?.pauseBriefingDefinition,
        };
      },
      mapBriefingDefinitionMutationResponse,
    );

/** @public */
export const resumeGraphQLBriefingDefinition: BriefingDefinitionRepository["resume"] =
  (_context, id) =>
    runBriefingDefinitionRequest(
      "resume",
      async () => {
        const result = await client
          .mutation(ResumeBriefingDefinition, { id })
          .toPromise();
        return {
          error: result.error,
          payload: result.data?.resumeBriefingDefinition,
        };
      },
      mapBriefingDefinitionMutationResponse,
    );

/** @public */
export const deleteGraphQLBriefingDefinition: BriefingDefinitionRepository["delete"] =
  (_context, id) =>
    runBriefingDefinitionRequest(
      "delete",
      async () => {
        const result = await client
          .mutation(DeleteBriefingDefinition, { id })
          .toPromise();
        return {
          error: result.error,
          payload: result.data?.deleteBriefingDefinition,
        };
      },
      mapBriefingDefinitionDeleteMutationResponse,
    );

/** @public */
export const graphqlBriefingDefinitionRepository: BriefingDefinitionRepository =
  {
    get: getGraphQLBriefingDefinition,
    create: createGraphQLBriefingDefinition,
    update: updateGraphQLBriefingDefinition,
    pause: pauseGraphQLBriefingDefinition,
    resume: resumeGraphQLBriefingDefinition,
    delete: deleteGraphQLBriefingDefinition,
  };
